import * as React from "react"
import { useTranslation } from "react-i18next"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Badge, Button } from "@nexus/ui"
import { toast } from "sonner"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions"
import { ApiError } from "@/lib/api-client"
import { invalidatePersonaOnboarding, personaOnboardingSuggestionsQueryOptions } from "@/lib/queries"
import {
  acceptPersonaOnboardingSuggestion,
  continuePersonaOnboarding,
  rejectPersonaOnboardingSuggestion,
  undoPersonaOnboardingSuggestion,
} from "../../api/personas.api"
import { PERSONA_ONBOARDING_DECISION_HOLD_MS } from "../../constants/personas.constants"
import { usePersonaOnboardingStore } from "../../store/personaOnboarding.store"
import type {
  Persona,
  PersonaOnboardingContinueSummary,
  PersonaOnboardingDecisionStatus,
  PersonaOnboardingReviewItem,
} from "../../types/personas.types"
import { PersonasOnboardingLoadingState } from "../PersonasOnboardingLoadingState/PersonasOnboardingLoadingState"
import { PersonasOnboardingReview } from "../PersonasOnboardingReview/PersonasOnboardingReview"

/** Local (client-only) decision for a SUGGESTED persona, applied on top of the fetched batch. */
interface LocalDecision {
  status: PersonaOnboardingDecisionStatus
  note: string | null
}

export interface PersonasOnboardingFlowProps {
  /** Called after Finish succeeds — parent shows the animated summary screen. */
  onFinished?: (summary: PersonaOnboardingContinueSummary, onboardingSessionId?: string) => void
  /** Leaves the carousel for the library. Review is optional, so this is always available. */
  onExit?: () => void
}

/** Review carousel — brand accept/reject hit the API; industry decisions are local-only (no dedicated accept/reject endpoints yet). */
export function PersonasOnboardingFlow({ onFinished, onExit }: PersonasOnboardingFlowProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const activeTenantId = useAuth((s) => s.activeTenantId)
  const perms = usePermissions()
  const canAccept = useIamPermission(perms.PERSONAS_ONBOARDING_ACCEPT)
  const canReject = useIamPermission(perms.PERSONAS_ONBOARDING_REJECT)
  // Undo works with its own grant, or with accept/reject (same review audience).
  const canUndo =
    useIamPermission(perms.PERSONAS_ONBOARDING_UNDO) || canAccept || canReject
  const canContinuePerm = useIamPermission(perms.PERSONAS_ONBOARDING_CONTINUE)
  const canSuggestions = useIamPermission(perms.PERSONAS_ONBOARDING_SUGGESTIONS)

  const focusIndex = usePersonaOnboardingStore((s) => s.focusIndex)
  const step = usePersonaOnboardingStore((s) => s.step)
  const sessionId = usePersonaOnboardingStore((s) => s.sessionId)
  const setFocusIndex = usePersonaOnboardingStore((s) => s.setFocusIndex)
  const setStep = usePersonaOnboardingStore((s) => s.setStep)
  const startSession = usePersonaOnboardingStore((s) => s.startSession)
  const resetUi = usePersonaOnboardingStore((s) => s.resetUi)

  const resolveSessionId = React.useCallback((): string | undefined => {
    if (!activeTenantId) return undefined
    return sessionId ?? startSession(activeTenantId)
  }, [activeTenantId, sessionId, startSession])

  // Mints once per review batch (covers both the Intro "Analyze my brand" start and
  // resuming straight into the carousel); reused for every reject until Finish.
  React.useEffect(() => {
    if (!activeTenantId || sessionId) return
    startSession(activeTenantId)
  }, [activeTenantId, sessionId, startSession])

  const suggestionsQuery = useQuery(
    personaOnboardingSuggestionsQueryOptions(activeTenantId, canSuggestions),
  )
  const suggested = suggestionsQuery.data?.suggested ?? []
  const brandSuggested = React.useMemo(
    () => suggested.filter((persona) => persona.type !== "INDUSTRY_SUGGESTED"),
    [suggested],
  )
  const industrySuggested = React.useMemo(
    () => suggested.filter((persona) => persona.type === "INDUSTRY_SUGGESTED"),
    [suggested],
  )

  // Local decisions overlay the fetched batch for this review session — accept/reject
  // already mutated the DB, but we keep the card visible (with its decision) until the
  // hold timer advances the carousel, instead of yanking it out of the list mid-review.
  const [brandDecisions, setBrandDecisions] = React.useState<Record<string, LocalDecision>>({})
  const [industryDecisions, setIndustryDecisions] = React.useState<Record<string, LocalDecision>>(
    {},
  )
  /**
   * Session snapshot of brand suggestions — survives refetch after accept/reject so
   * industry → Back can restore the carousel for undo / change-of-mind.
   */
  const [brandPersonas, setBrandPersonas] = React.useState<Persona[]>([])
  /** Snapshot of this review batch's industry suggestions, taken when brand review is finished. */
  const [industryPersonas, setIndustryPersonas] = React.useState<Persona[]>([])
  /** Brand cards removed after a 404 — excluded from snapshot seeding until refetch catches up. */
  const [dismissedBrandPersonaIds, setDismissedBrandPersonaIds] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  )

  const reviewableBrandSuggested = React.useMemo(
    () => brandSuggested.filter((persona) => !dismissedBrandPersonaIds.has(persona.id)),
    [brandSuggested, dismissedBrandPersonaIds],
  )

  // Tenant switch — drop stale local decisions from the previous tenant's batch.
  React.useEffect(() => {
    setBrandDecisions({})
    setIndustryDecisions({})
    setBrandPersonas([])
    setIndustryPersonas([])
    setDismissedBrandPersonaIds(new Set())
    resetUi()
    if (activeTenantId) startSession(activeTenantId)
  }, [activeTenantId, resetUi, startSession])

  React.useEffect(() => () => resetUi(), [resetUi])

  // Seed brand snapshot once per batch while suggestions still include brand rows.
  React.useEffect(() => {
    if (brandPersonas.length > 0) return
    if (reviewableBrandSuggested.length === 0) return
    setBrandPersonas(reviewableBrandSuggested)
  }, [brandPersonas.length, reviewableBrandSuggested])

  // Refresh mid-review: accepted brand personas leave SUGGESTED, so the list may
  // only have INDUSTRY_SUGGESTED left. Resume on industry instead of the brand empty state.
  React.useEffect(() => {
    if (!suggestionsQuery.isSuccess) return
    if (step !== "brand") return
    if (brandPersonas.length > 0 || reviewableBrandSuggested.length > 0) return
    if (industrySuggested.length === 0) return
    if (industryPersonas.length > 0) {
      setFocusIndex(0)
      setStep("industry")
      return
    }
    setIndustryPersonas(industrySuggested)
    setIndustryDecisions({})
    setFocusIndex(0)
    setStep("industry")
  }, [
    brandPersonas.length,
    reviewableBrandSuggested.length,
    industryPersonas.length,
    industrySuggested,
    setFocusIndex,
    setStep,
    step,
    suggestionsQuery.isSuccess,
  ])

  const brandItems = React.useMemo<PersonaOnboardingReviewItem[]>(
    () =>
      brandPersonas.map((persona) => ({
        persona,
        decisionStatus: brandDecisions[persona.id]?.status ?? "PENDING",
        rejectionNote: brandDecisions[persona.id]?.note ?? null,
      })),
    [brandPersonas, brandDecisions],
  )

  const industryItems = React.useMemo<PersonaOnboardingReviewItem[]>(
    () =>
      industryPersonas.map((persona) => ({
        persona,
        decisionStatus: industryDecisions[persona.id]?.status ?? "PENDING",
        rejectionNote: industryDecisions[persona.id]?.note ?? null,
      })),
    [industryPersonas, industryDecisions],
  )

  const items = step === "industry" ? industryItems : brandItems

  // Null when there is nothing to measure. A resume straight into the industry step
  // has no brand pool left, and reporting 0% there would tell the reviewer they
  // earned no coverage rather than that it is simply unknown.
  const brandMarketCoveragePct = React.useMemo(() => {
    const acceptedShare = (measure: (item: PersonaOnboardingReviewItem) => number) => {
      const pool = brandItems.reduce((sum, item) => sum + measure(item), 0)
      if (pool <= 0) return null
      const accepted = brandItems
        .filter((item) => item.decisionStatus === "ACCEPTED")
        .reduce((sum, item) => sum + measure(item), 0)
      return Math.round((accepted / pool) * 100)
    }
    return acceptedShare((item) => item.persona.adCount) ?? acceptedShare((item) => item.persona.spend)
  }, [brandItems])

  const advanceTimerRef = React.useRef<number | null>(null)

  const clearScheduledAdvance = React.useCallback(() => {
    if (advanceTimerRef.current == null) return
    window.clearTimeout(advanceTimerRef.current)
    advanceTimerRef.current = null
  }, [])

  const scheduleAdvanceAfterDecision = React.useCallback(() => {
    clearScheduledAdvance()
    advanceTimerRef.current = window.setTimeout(() => {
      advanceTimerRef.current = null
      if (items.length <= 1) return
      // Read the live index rather than the one captured at schedule time — the
      // reviewer may have navigated during the hold, and advancing from a stale
      // index yanks focus off the card they are now on.
      const next = usePersonaOnboardingStore.getState().focusIndex + 1
      setFocusIndex(next < items.length ? next : 0)
    }, PERSONA_ONBOARDING_DECISION_HOLD_MS)
  }, [clearScheduledAdvance, items.length, setFocusIndex])

  React.useEffect(() => () => clearScheduledAdvance(), [clearScheduledAdvance])

  /** Manual navigation wins over a pending auto-advance. */
  const handleFocusIndexChange = React.useCallback(
    (index: number) => {
      clearScheduledAdvance()
      setFocusIndex(index)
    },
    [clearScheduledAdvance, setFocusIndex],
  )

  /** Finish is the only point that refreshes status/suggestions/list — mid-review
   * refetches would replace the carousel out from under the reviewer. */
  const invalidateOnboarding = React.useCallback(async () => {
    if (!activeTenantId) return
    await invalidatePersonaOnboarding(queryClient, activeTenantId)
  }, [activeTenantId, queryClient])

  /** Drop a card the server no longer treats as SUGGESTED and keep focus on a valid index. */
  const handleBrandDecisionNotReviewable = React.useCallback(
    async (personaId: string) => {
      clearScheduledAdvance()
      setDismissedBrandPersonaIds((prev) => new Set(prev).add(personaId))

      setBrandDecisions((prev) => {
        const next = { ...prev }
        delete next[personaId]
        return next
      })

      const shouldAdvanceToIndustry =
        brandPersonas.some((persona) => persona.id === personaId) &&
        brandPersonas.filter((persona) => persona.id !== personaId).length === 0 &&
        industrySuggested.length > 0

      setBrandPersonas((prev) => {
        const removedIndex = prev.findIndex((persona) => persona.id === personaId)
        if (removedIndex === -1) return prev
        const next = prev.filter((persona) => persona.id !== personaId)
        const newLength = next.length
        const currentIndex = usePersonaOnboardingStore.getState().focusIndex

        if (newLength === 0) {
          setFocusIndex(0)
        } else if (currentIndex > removedIndex) {
          setFocusIndex(currentIndex - 1)
        } else if (currentIndex >= newLength) {
          setFocusIndex(newLength - 1)
        }

        return next
      })

      if (shouldAdvanceToIndustry) {
        if (industryPersonas.length === 0) {
          setIndustryPersonas(industrySuggested)
          setIndustryDecisions({})
        }
        setFocusIndex(0)
        setStep("industry")
      }

      toast.error(
        t(
          "personas.onboarding.review.noLongerReviewable",
          "This persona is no longer available — moving on.",
        ),
      )

      await invalidateOnboarding()
      await suggestionsQuery.refetch()
    },
    [
      brandPersonas,
      clearScheduledAdvance,
      industryPersonas.length,
      industrySuggested,
      invalidateOnboarding,
      setFocusIndex,
      setStep,
      suggestionsQuery,
      t,
    ],
  )

  const handleBrandDecisionError = React.useCallback(
    (error: unknown, personaId: string) => {
      if (error instanceof ApiError && error.status === 404) {
        void handleBrandDecisionNotReviewable(personaId)
        return
      }
      toast.error(
        t("personas.onboarding.review.decisionFailed", "Couldn't save your decision — try again."),
      )
    },
    [handleBrandDecisionNotReviewable, t],
  )

  const acceptMut = useMutation({
    mutationFn: acceptPersonaOnboardingSuggestion,
    meta: { skipGlobalError: true },
    onSuccess: (_data, vars) => {
      setBrandDecisions((prev) => ({ ...prev, [vars.id]: { status: "ACCEPTED", note: null } }))
      scheduleAdvanceAfterDecision()
    },
    onError: (error, vars) => {
      handleBrandDecisionError(error, vars.id)
    },
  })

  const rejectMut = useMutation({
    mutationFn: rejectPersonaOnboardingSuggestion,
    meta: { skipGlobalError: true },
    onSuccess: (_data, vars) => {
      setBrandDecisions((prev) => ({
        ...prev,
        [vars.id]: { status: "REJECTED", note: vars.rejectionReason?.trim() || null },
      }))
      scheduleAdvanceAfterDecision()
    },
    onError: (error, vars) => {
      handleBrandDecisionError(error, vars.id)
    },
  })

  const undoMut = useMutation({
    mutationFn: undoPersonaOnboardingSuggestion,
    onMutate: (vars) => {
      clearScheduledAdvance()
      const previous = brandDecisions[vars.id]
      setBrandDecisions((prev) => {
        const next = { ...prev }
        delete next[vars.id]
        return next
      })
      return { id: vars.id, previous }
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return
      setBrandDecisions((prev) => (ctx.previous ? { ...prev, [ctx.id]: ctx.previous } : prev))
      toast.error(t("personas.onboarding.review.undoFailed", "Couldn't undo — try again."))
    },
  })

  const continueMut = useMutation({
    mutationFn: (vars: { onboardingSessionId?: string }) => {
      // Industry decisions are local until Finish — send both explicit verdicts
      // so the server commits them in one transaction. Cards the reviewer never
      // touched are in neither list and stay New (AD-2518).
      const acceptIds = industryItems
        .filter((item) => item.decisionStatus === "ACCEPTED")
        .map((item) => item.persona.id)
      const rejectIds = industryItems
        .filter((item) => item.decisionStatus === "REJECTED")
        .map((item) => item.persona.id)
      return continuePersonaOnboarding({ ...vars, acceptIds, rejectIds })
    },
    onSuccess: async (data, vars) => {
      clearScheduledAdvance()
      setBrandDecisions({})
      setIndustryDecisions({})
      setBrandPersonas([])
      setIndustryPersonas([])
      resetUi()
      onFinished?.(data.summary, vars.onboardingSessionId ?? resolveSessionId())
      await invalidateOnboarding()
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "PERSONAS_SYNC_IN_FLIGHT") {
        toast.error(
          t(
            "personas.toast.syncInProgress",
            "Persona sync is still running — wait a moment and try again.",
          ),
        )
        return
      }
      toast.error(t("personas.toast.finishFailed", "Couldn't finish setup. Please try again."))
    },
  })

  const isBrandBusy = acceptMut.isPending || rejectMut.isPending || undoMut.isPending
  const isBusy = step === "brand" && isBrandBusy

  /** Industry picks are staged client-side until Finish; brand picks hit the API per decision. */
  const applyIndustryDecision = React.useCallback(
    (personaId: string, status: PersonaOnboardingDecisionStatus) => {
      setIndustryDecisions((prev) => ({ ...prev, [personaId]: { status, note: null } }))
      scheduleAdvanceAfterDecision()
    },
    [scheduleAdvanceAfterDecision],
  )

  const handleAccept = React.useCallback(
    (item: PersonaOnboardingReviewItem) => {
      if (step === "industry") {
        applyIndustryDecision(item.persona.id, "ACCEPTED")
        return
      }
      if (!canAccept) return
      acceptMut.mutate({ id: item.persona.id })
    },
    [acceptMut, applyIndustryDecision, canAccept, step],
  )

  const handleReject = React.useCallback(
    (item: PersonaOnboardingReviewItem) => {
      if (step === "industry") {
        applyIndustryDecision(item.persona.id, "REJECTED")
        return
      }
      if (!canReject) return
      rejectMut.mutate({ id: item.persona.id, onboardingSessionId: resolveSessionId() })
    },
    [applyIndustryDecision, canReject, rejectMut, resolveSessionId, step],
  )

  const handleUndo = React.useCallback(
    (item: PersonaOnboardingReviewItem) => {
      if (step === "industry") {
        clearScheduledAdvance()
        setIndustryDecisions((prev) => {
          const next = { ...prev }
          delete next[item.persona.id]
          return next
        })
        return
      }
      if (!canUndo) return
      undoMut.mutate({ id: item.persona.id })
    },
    [canUndo, clearScheduledAdvance, step, undoMut],
  )

  const handleContinueToIndustry = React.useCallback(() => {
    clearScheduledAdvance()
    // Only the first entry into a batch seeds the snapshot. Re-entering after
    // Back must keep the staged adds — reseeding here silently discarded every
    // industry pick the reviewer had already made.
    if (industryPersonas.length === 0) {
      setIndustryPersonas(industrySuggested)
      setIndustryDecisions({})
    }
    setFocusIndex(0)
    setStep("industry")
  }, [clearScheduledAdvance, industryPersonas.length, industrySuggested, setFocusIndex, setStep])

  const handleBackToBrand = React.useCallback(() => {
    clearScheduledAdvance()
    setFocusIndex(0)
    setStep("brand")
  }, [clearScheduledAdvance, setFocusIndex, setStep])

  // Brand verdicts are already committed; staged industry picks are not, and leaving
  // discards them. The refresh the carousel suppresses mid-review has to happen here
  // so the library banner reflects the decisions made before the exit.
  const handleExit = React.useCallback(() => {
    clearScheduledAdvance()
    void invalidateOnboarding()
    onExit?.()
  }, [clearScheduledAdvance, invalidateOnboarding, onExit])

  const canContinueBrand = canContinuePerm && !continueMut.isPending

  /** Industry finish is optional — user may skip every card. */
  const canFinishIndustry = canContinuePerm && !continueMut.isPending

  const continueHint = React.useMemo(() => {
    if (brandItems.length === 0) return t("personas.onboarding.review.hintEmpty", "No brand personas were found to review.")
    const pendingCount = brandItems.filter((i) => i.decisionStatus === "PENDING").length
    if (pendingCount > 0) {
      return t(
        "personas.onboarding.review.hintPending",
        "Personas you skip stay in your library as New — you can review them later.",
      )
    }
    return t("personas.onboarding.review.hintReady", "Ready — continue to industry personas.")
  }, [brandItems, t])

  if (suggestionsQuery.isLoading) {
    return <PersonasOnboardingLoadingState />
  }

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="personas-onboarding-flow" data-step={step}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              {t("personas.title", "User Personas")}
            </h1>
            <Badge variant="status-delivered" dot={false} iconStart="plus">
              {t("personas.onboarding.badge", "Setup")}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {step === "industry"
              ? t("personas.onboarding.header.industrySubtitle", "Step 2 of 2 — add relevant personas from across your market.")
              : t("personas.onboarding.header.reviewSubtitle", "Swipe through the customer types in your own ads.")}
          </p>
        </div>

        {onExit ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconStart="arrow-left"
            onClick={handleExit}
            disabled={continueMut.isPending}
            data-testid="personas-onboarding-exit"
            className="min-h-11 shrink-0 [@media(hover:hover)_and_(pointer:fine)]:min-h-8"
          >
            {t("personas.onboarding.exitToLibrary", "Back to library")}
          </Button>
        ) : null}
      </div>

      <PersonasOnboardingReview
        variant={step}
        items={items}
        focusIndex={focusIndex}
        onFocusIndexChange={handleFocusIndexChange}
        isBusy={isBusy}
        canContinue={step === "industry" ? canFinishIndustry : canContinueBrand}
        continueHint={continueHint}
        marketCoveragePct={brandMarketCoveragePct}
        onAccept={handleAccept}
        onReject={handleReject}
        onUndo={handleUndo}
        onContinue={
          step === "industry"
            ? () => continueMut.mutate({ onboardingSessionId: resolveSessionId() })
            : handleContinueToIndustry
        }
        onBack={
          step === "industry" && brandPersonas.length > 0 ? handleBackToBrand : undefined
        }
        isContinuing={continueMut.isPending}
        onRetry={
          step === "brand"
            ? () => {
                void suggestionsQuery.refetch()
              }
            : undefined
        }
        isRetrying={suggestionsQuery.isFetching}
      />
    </div>
  )
}
